// Session state (prototype `web/src/lib/auth.tsx`), extended for one account in many tenants:
// `switchTenant` exchanges the token for one scoped to another tenant.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { api, post, refreshSession, session, setUnauthorizedHandler } from "./api";
import { locale, setLocale } from "./i18n";
import { can as canPerm } from "./permissions";
import type { Company, Me, TokenResponse } from "./types";

interface Auth {
  me: Me;
  company: Company;
  can: (permission: string) => boolean;
  switchCompany: (id: string) => void;
  switchTenant: (id: string) => Promise<void>;
  logout: () => void;
}

const AuthCtx = createContext<Auth | null>(null);
export const useAuth = () => useContext(AuthCtx)!;

/** Sign in; `code` is the authenticator code once the server answers `mfa_required`. */
export async function login(email: string, password: string, code?: string) {
  const t = await post<TokenResponse>("/auth/login", { email, password, code: code || undefined });
  session.token = t.token;
  session.signedIn = true;
  session.company = null; // pick a valid company for this tenant after /auth/me
  return t;
}

export function AuthProvider({
  children,
  fallback,
  noTenant,
}: {
  children: ReactNode;
  fallback: ReactNode;
  noTenant: ReactNode;
}) {
  const qc = useQueryClient();
  // Back from single sign-on: the server set the session cookie; restore it.
  useState(() => {
    const u = new URL(location.href);
    if (u.searchParams.get("sso") === "1") {
      session.signedIn = true;
      u.searchParams.delete("sso");
      history.replaceState(null, "", u.pathname + u.search);
    }
  });
  const [token, setToken] = useState(session.token);
  // A new tab restores the cookie session once before showing the sign-in page.
  const [restoring, setRestoring] = useState(!session.token && session.signedIn);
  useEffect(() => {
    if (!restoring) return;
    void refreshSession().then((outcome) => {
      if (outcome === "ok") setToken(session.token);
      // Not on "offline": a reload that cancelled this request would otherwise sign the user out.
      else if (outcome === "refused") session.signedIn = false;
      setRestoring(false);
    });
  }, [restoring]);
  const [companyId, setCompanyId] = useState(session.company);
  const logout = useCallback(() => {
    if (session.signedIn) void post("/auth/logout").catch(() => {});
    session.token = null;
    session.signedIn = false;
    session.company = null;
    setToken(null);
    setCompanyId(null);
    qc.clear();
  }, [qc]);
  useEffect(() => setUnauthorizedHandler(logout), [logout]);

  const me = useQuery({
    queryKey: ["me", token],
    queryFn: () => api<Me>("/auth/me"),
    enabled: !!token,
    staleTime: 60_000,
  });

  // Keep the stored company valid for this tenant.
  const companies = me.data?.companies ?? [];
  const company =
    companies.find((c) => c.id === companyId) ??
    companies.find((c) => c.id === me.data?.companyId) ??
    companies[0];
  useEffect(() => {
    if (company && company.id !== session.company) {
      session.company = company.id;
      setCompanyId(company.id);
    }
  }, [company]);

  // The person's saved language wins over this browser's (one reload when it differs).
  useEffect(() => {
    if (me.data?.locale && me.data.locale !== locale) setLocale(me.data.locale);
  }, [me.data?.locale]);

  // Any failure to load the session (expired token, removed access, server down) falls back to sign-in.
  useEffect(() => {
    if (me.isError) logout();
  }, [me.isError, logout]);
  if (restoring) return null;
  if (!token) return <LoginGate onLogin={() => setToken(session.token)}>{fallback}</LoginGate>;
  if (!me.data) return null;
  if (!me.data.tenant || !company)
    return (
      <TenantGate me={me.data} logout={logout} onSwitch={() => setToken(session.token)}>
        {noTenant}
      </TenantGate>
    );

  const switchTenant = async (id: string) => {
    const t = await post<TokenResponse>("/auth/switch-tenant", { tenantId: id });
    session.token = t.token;
    session.company = null;
    setCompanyId(null);
    qc.clear();
    setToken(t.token);
  };
  const value: Auth = {
    me: me.data,
    company,
    logout,
    switchTenant,
    can: (p) => canPerm(me.data.permissions, p),
    switchCompany: (id) => {
      session.company = id;
      setCompanyId(id);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
    },
  };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

const LoginCtx = createContext<() => void>(() => {});
export const useOnLogin = () => useContext(LoginCtx);
function LoginGate({ onLogin, children }: { onLogin: () => void; children: ReactNode }) {
  return <LoginCtx.Provider value={onLogin}>{children}</LoginCtx.Provider>;
}

/** Signed in without a tenant (an operator with no memberships): pick one or use the console. */
const TenantGateCtx = createContext<{ me: Me | null; logout: () => void; onSwitch: () => void }>({
  me: null,
  logout: () => {},
  onSwitch: () => {},
});
export const useTenantGate = () => useContext(TenantGateCtx);
function TenantGate({
  me,
  logout,
  onSwitch,
  children,
}: {
  me: Me;
  logout: () => void;
  onSwitch: () => void;
  children: ReactNode;
}) {
  return <TenantGateCtx.Provider value={{ me, logout, onSwitch }}>{children}</TenantGateCtx.Provider>;
}
